import { useEffect, useMemo, useState } from "react";
import useAxiosClient from "../axios-client";
import { balanceMap, clothTotals, onHandForLine } from "../components/Stock/fabricShelves";

/**
 * What the raw-fabric store holds, for forms that only want to *say* so.
 *
 * The order form promises nothing about stock — a line may be for cloth that
 * has yet to be delivered — so this is an aid, not a gate: a failed fetch
 * leaves the readout off rather than raising a toast over a form the operator
 * is in the middle of. That is also why nothing here is a loading state anyone
 * waits on.
 *
 * The whole balances payload is one small array (a few dozen shelves) and every
 * product line asks about a different one, so it is fetched once per form
 * rather than per line.
 *
 * @returns {{ onHand: (fabricTypeId, fabricColorId, fabricPatternId) => ({weight, rolls, prints, exact}|null), known: boolean }}
 */
export const useFabricBalances = () => {
    const axiosClient = useAxiosClient();
    // null until an answer arrives: "not fetched yet" and "nothing in stock"
    // must not read the same, or a form would announce an empty store while the
    // request is still in the air.
    const [rows, setRows] = useState(null);

    useEffect(() => {
        let live = true;
        axiosClient.get('/fabricStock/balances')
            .then((response) => { if (live) setRows(response.data || []); })
            .catch(() => { /* silent — see the note above */ });
        return () => { live = false; };
    }, [axiosClient]);

    const index = useMemo(
        () => (rows ? { byShelf: balanceMap(rows), byCloth: clothTotals(rows) } : null),
        [rows],
    );

    const onHand = useMemo(
        () => (fabricTypeId, fabricColorId, fabricPatternId) => (
            index ? onHandForLine(index, fabricTypeId, fabricColorId, fabricPatternId) : null
        ),
        [index],
    );

    return { onHand, known: index !== null };
};
